import { Descriptions, Table, TableColumnsType } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getCounsels } from "../../../api/studentsCounsel.api";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import { getCommonCodeLabel } from "../../../lib/store";
import { IClass } from "../../../models/campusClasses.model";
import { ICounselItem, IGetCounselList } from "../../../models/studentsCounsel.model";
import { ClassContents } from "../../../pages/tutor/class";
import Button from "../../Button";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import Pagination from "../../Pagination";
import SelectCounselType from "../../student/consult/SelectCounselType";

interface IProps {
  classData: IClass|undefined;
  setContent: (content: ClassContents) => void;
  setCounselCode: (counselCode: number) => void;
}

const Counseling: FC<IProps> = ({
  classData,
  setContent,
  setCounselCode,
}) => {
  const { t } = useTranslation();
  const { data: res, isLoading, error, execute } = useAxios<IGetCounselList>();

  const [classCode, setClassCode] = useState<string>();
  const [pageNo, setPageNo] = useState(1);
  const [counselGBN, setCounselGBN] = useState<string>();

  useEffect(() => {
    if (classData?.classCode) {
      setClassCode(classData.classCode);
    }
  }, [classData]);

  useEffect(() => {
    if (classCode) {
      const params: any = {
        perPage: config.pageSize,
        pageNo,
        classCode,
      };
      if (counselGBN) params.counselGBN = counselGBN;

      execute(getCounsels(params));
    }
  }, [classCode, pageNo, counselGBN]);

  const onSetCounselGBN = (value: string) => {
    setCounselGBN(value);
    setPageNo(1);
  }

  const columns: TableColumnsType<ICounselItem> = [
    {
      title: t('class.tutor.column4'),
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'center',
      sorter: (a, b) => a.studentName?.localeCompare(b.studentName),
    },
    {
      title: t('class.tutor.column11'),
      dataIndex: 'counselDate',
      key: 'counselDate',
      align: 'center',
      sorter: (a, b) => a.counselDate?.localeCompare(b.counselDate),
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('class.tutor.column12'),
      dataIndex: 'counselTargetGBN',
      key: 'counselTargetGBN',
      align: 'center',
      sorter: (a, b) => a.counselTargetGBN?.localeCompare(b.counselTargetGBN),
      render: value => getCommonCodeLabel('MB0004', value, 'ko'),
    },
    {
      title: t('class.tutor.column13'),
      dataIndex: 'counselChannelCode',
      key: 'counselChannelCode',
      align: 'center',
      sorter: (a, b) => a.counselChannelCode?.localeCompare(b.counselChannelCode),
      render: value => getCommonCodeLabel('MB0007', value, 'ko'),
    },
    {
      title: t('class.tutor.column14'),
      dataIndex: 'counselGBN',
      key: 'counselGBN',
      align: 'center',
      sorter: (a, b) => a.counselGBN?.localeCompare(b.counselGBN),
      render: (value: string) => getCommonCodeLabel('MB0008', value, 'ko'),
    },
    {
      title: t('class.tutor.column15'),
      dataIndex: 'counselTitle',
      key: 'counselTitle',
      align: 'center',
      sorter: (a, b) => a.counselTitle?.localeCompare(b.counselTitle),
    },
    {
      title: t('class.tutor.column16'),
      dataIndex: 'writerName',
      key: 'writerName',
      align: 'center',
      sorter: (a, b) => a.writerName?.localeCompare(b.writerName),
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className='comm-table__area rowType mt-2'>
        <Descriptions
          bordered
          column={4}
          size="small"
          labelStyle={{ width: '12.5%' }}
          contentStyle={{ width: '12.5%' }}
        >
          <Descriptions.Item label={t('class.tutor.column17')}>
            {classData?.courseDetailName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.tutor.column18')}>
            {classData?.courseName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.tutor.column19')}>
            {classData?.courseSubName}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.tutor.column14')}>
            <SelectCounselType
              counselGBN={counselGBN}
              setCounselGBN={onSetCounselGBN}
            />
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="mt-4 comm-btn__area right">
        <Button
          className="btn-basic pink"
          onClick={() => setContent(ClassContents.CreateCounseling)}
          text={t('class.tutor.button4')}
        />
      </div>

      <div>
        <Table
          className='comm-table__area mt-2'
          columns={columns}
          dataSource={res?.data}
          pagination={{
            current: pageNo,
            pageSize: config.pageSize,
          }}
          onRow={record => {
            return {
              onClick: () => {
                setCounselCode(record.counselCode);
                setContent(ClassContents.ReadCounseling);
              },
            };
          }}          
          rowClassName="pointer"
          rowKey="counselCode"
        />
      </div>

      <div className="comm-bx__between">
        <div />
        <div className="common-paging center">
          <Pagination
            count={res?.data.length || 0}
            page={pageNo}
            setPage={setPageNo}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={() => setContent(ClassContents.CreateCounseling)}
            text={t('class.tutor.button4')}
          />
        </div>
      </div>      
    </>
  );
}

export default Counseling;
